import type { NappyType, StoolColour, StoolTexture } from '@baby-tracker/validation';
import { stoolColourSchema, stoolTextureSchema } from '@baby-tracker/validation';
import { useState, type ReactElement } from 'react';

import { ChoiceChips } from '../../ui/ChoiceChips.js';
import { FormError } from '../../ui/FormError.js';
import { SelectField } from '../../ui/SelectField.js';
import { Sheet } from '../../ui/Sheet.js';
import fieldStyles from '../../ui/fields.module.css';
import styles from './tracking.module.css';

const kindOptions = [
    { value: 'wet', label: 'Wet' },
    { value: 'dirty', label: 'Dirty' },
    { value: 'mixed', label: 'Mixed' },
    { value: 'dry', label: 'Dry' },
] as const satisfies readonly { value: NappyType; label: string }[];

const colourOptions = [
    { value: '', label: 'Not recorded' },
    { value: 'yellow', label: 'Yellow' },
    { value: 'mustard', label: 'Mustard' },
    { value: 'green', label: 'Green' },
    { value: 'brown', label: 'Brown' },
    { value: 'black', label: 'Black' },
    { value: 'red', label: 'Red' },
    { value: 'white', label: 'White' },
    { value: 'other', label: 'Other' },
] as const;

const textureOptions = [
    { value: '', label: 'Not recorded' },
    { value: 'runny', label: 'Runny' },
    { value: 'soft', label: 'Soft' },
    { value: 'seedy', label: 'Seedy' },
    { value: 'mucousy', label: 'Mucousy' },
    { value: 'formed', label: 'Formed' },
    { value: 'pebbles', label: 'Pebbles' },
    { value: 'other', label: 'Other' },
] as const;

export interface NappySave {
    kind: NappyType;
    hasRash: boolean;
    stoolColour?: StoolColour | undefined;
    stoolTexture?: StoolTexture | undefined;
}

export function NappySheet({
    open,
    pending,
    error,
    onClose,
    onSave,
}: {
    open: boolean;
    pending: boolean;
    error: string | null;
    onClose: () => void;
    onSave: (input: NappySave) => Promise<void>;
}): ReactElement {
    const [kind, setKind] = useState<NappyType | null>(null);
    const [colour, setColour] = useState('');
    const [texture, setTexture] = useState('');
    const [hasRash, setHasRash] = useState(false);

    function payload(nextKind: NappyType): NappySave {
        const includeStool = nextKind === 'dirty' || nextKind === 'mixed';
        const colourParsed = stoolColourSchema.safeParse(colour);
        const textureParsed = stoolTextureSchema.safeParse(texture);
        return {
            kind: nextKind,
            hasRash,
            ...(includeStool && colourParsed.success ? { stoolColour: colourParsed.data } : {}),
            ...(includeStool && textureParsed.success ? { stoolTexture: textureParsed.data } : {}),
        };
    }

    return (
        <Sheet title="Nappy" open={open} onClose={onClose}>
            <ChoiceChips
                label="Type"
                value={kind}
                options={kindOptions}
                onChange={(value) => {
                    if (pending) {
                        return;
                    }
                    setKind(value);
                    void onSave(payload(value));
                }}
            />
            <div className={fieldStyles.stack}>
                <SelectField
                    label="Colour"
                    options={colourOptions}
                    value={colour}
                    onChange={(event) => {
                        setColour(event.target.value);
                    }}
                />
                <SelectField
                    label="Texture"
                    options={textureOptions}
                    value={texture}
                    onChange={(event) => {
                        setTexture(event.target.value);
                    }}
                />
                <label className={styles.check}>
                    <input
                        type="checkbox"
                        checked={hasRash}
                        onChange={(event) => {
                            setHasRash(event.target.checked);
                        }}
                    />
                    Rash
                </label>
            </div>
            <FormError message={error} />
        </Sheet>
    );
}
